import parse from 'date-fns/parse';
import getHours from 'date-fns/getHours';
import getMinutes from 'date-fns/getMinutes';
import timeZoneData from './timezones.json';

export const defaultTimeSlot = [
  {
    day: 0,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 1,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 2,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 3,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 4,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 5,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 6,
    to: '',
    from: '',
    valid: false,
  },
];

export const generateTimeSlots = (step = 15) => {
  /* 
    Generates a list of time strings from 12:00 AM to next 24 hours. Each new string
     will be generated by adding `step` minutes to the previous one.
    The list is generated by starting with a random day and adding step minutes till end of the same day.
    Always includes 11:59 PM as the final slot to complete the day.
  */
  const date = new Date(1970, 1, 1);
  const slots = [];
  while (date.getDate() === 1) {
    slots.push(
      date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    );
    date.setMinutes(date.getMinutes() + step);
  }

  // Always add 11:59 PM as the final slot if it's not already included
  const lastSlot = '11:59 PM';
  if (!slots.includes(lastSlot)) {
    slots.push(lastSlot);
  }

  return slots;
};

export const getTime = (hour, minute) => {
  const merdian = hour > 11 ? 'PM' : 'AM';
  const modHour = hour > 12 ? hour % 12 : hour || 12;
  const parsedHour = modHour < 10 ? `0${modHour}` : modHour;
  const parsedMinute = minute < 10 ? `0${minute}` : minute;
  return `${parsedHour}:${parsedMinute} ${merdian}`;
};

export const timeSlotParse = timeSlots => {
  return timeSlots.map(slot => {
    const {
      day_of_week: day,
      open_hour: openHour,
      open_minutes: openMinutes,
      close_hour: closeHour,
      close_minutes: closeMinutes,
      closed_all_day: closedAllDay,
      open_all_day: openAllDay,
    } = slot;
    const from = closedAllDay ? '' : getTime(openHour, openMinutes);
    const to = closedAllDay ? '' : getTime(closeHour, closeMinutes);

    return {
      day,
      to,
      from,
      valid: !closedAllDay,
      openAllDay,
    };
  });
};

export const timeSlotTransform = timeSlots => {
  return timeSlots.map(slot => {
    const closed = slot.openAllDay ? false : !(slot.to && slot.from);
    const openAllDay = slot.openAllDay;
    let fromDate = '';
    let toDate = '';
    let openHour = '';
    let openMinutes = '';
    let closeHour = '';
    let closeMinutes = '';

    if (!closed) {
      fromDate = parse(slot.from, 'hh:mm a', new Date());
      toDate = parse(slot.to, 'hh:mm a', new Date());
      openHour = getHours(fromDate);
      openMinutes = getMinutes(fromDate);
      closeHour = getHours(toDate);
      closeMinutes = getMinutes(toDate);
    }

    return {
      day_of_week: slot.day,
      closed_all_day: closed,
      open_hour: openHour,
      open_minutes: openMinutes,
      close_hour: closeHour,
      close_minutes: closeMinutes,
      open_all_day: openAllDay,
    };
  });
};

export const timeZoneOptions = () => {
  return Object.keys(timeZoneData).map(key => ({
    label: key,
    value: timeZoneData[key],
  }));
};

const UTC_TIME_ZONES = new Set(['UTC', 'Etc/UTC']);

export const normalizeTimeZoneValue = value => {
  if (!value) return '';
  return UTC_TIME_ZONES.has(value) ? 'Etc/UTC' : value;
};

export const timeZonesEquivalent = (left, right) => {
  if (!left || !right) return left === right;
  if (left === right) return true;
  return UTC_TIME_ZONES.has(left) && UTC_TIME_ZONES.has(right);
};

export const resolveTimeZoneOption = (timeZones, timeZone, fallback) => {
  if (!timeZone) return fallback;
  const exact = timeZones.find(item => item.value === timeZone);
  if (exact) return exact;
  if (UTC_TIME_ZONES.has(timeZone)) {
    const alias = timeZones.find(item => UTC_TIME_ZONES.has(item.value));
    if (alias) return alias;
  }
  return { label: timeZone, value: timeZone };
};

export const DEFAULT_TIMEZONE = {
  label: 'Beijing (GMT+08:00)',
  value: 'Asia/Shanghai',
};

export const DEFAULT_DAILY_WORKING_HOURS = {
  open_hour: 10,
  open_minutes: 0,
  close_hour: 21,
  close_minutes: 0,
};

export const parseDailyWorkingHours = (hours = {}) => ({
  open_hour: Number(
    hours.open_hour ?? hours.openHour ?? DEFAULT_DAILY_WORKING_HOURS.open_hour
  ),
  open_minutes: Number(
    hours.open_minutes ??
      hours.openMinutes ??
      DEFAULT_DAILY_WORKING_HOURS.open_minutes
  ),
  close_hour: Number(
    hours.close_hour ?? hours.closeHour ?? DEFAULT_DAILY_WORKING_HOURS.close_hour
  ),
  close_minutes: Number(
    hours.close_minutes ??
      hours.closeMinutes ??
      DEFAULT_DAILY_WORKING_HOURS.close_minutes
  ),
});

export const dailyHoursToDisplay = (hours = {}) => {
  const parsed = parseDailyWorkingHours(hours);
  return {
    from: getTime(parsed.open_hour, parsed.open_minutes),
    to: getTime(parsed.close_hour, parsed.close_minutes),
  };
};

export const displayToDailyHours = (from, to) => {
  const fromDate = parse(from, 'hh:mm a', new Date());
  const toDate = parse(to, 'hh:mm a', new Date());
  return {
    open_hour: getHours(fromDate),
    open_minutes: getMinutes(fromDate),
    close_hour: getHours(toDate),
    close_minutes: getMinutes(toDate),
  };
};

export const getDateKeyInTimeZone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts
      .filter(part => ['year', 'month', 'day'].includes(part.type))
      .map(part => [part.type, part.value])
  );
  return `${values.year}-${values.month}-${values.day}`;
};

export const normalizeNonWorkingDates = (dates = []) =>
  [...new Set((dates || []).filter(Boolean).map(String))].sort();

export const getIsoWeekInfo = (year, month, day) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  const dayNumber = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNumber + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));

  return {
    week: 1 + Math.round((date - firstThursday) / (7 * 24 * 60 * 60 * 1000)),
    weekYear: date.getUTCFullYear(),
  };
};
